import React, { useRef, useState, useEffect } from "react";
import styled from "styled-components";
import { useTranslate } from "@tolgee/react";
import { ReactComponent as NoPollsIcon } from "../../Assets/iconsv2/generic/noPolls.svg";
import { ReactComponent as EditIcon } from "../../Assets/iconsv2/generic/editPencilIcon.svg";
import { ReactComponent as RadioIcon } from "../../Assets/iconsv2/generic/radioButtonIcon.svg";
import { ReactComponent as MultipleChoiceIcon } from "../../Assets/iconsv2/generic/checkBoxIcon.svg";
import { ReactComponent as DragIcon } from "../../Assets/iconsv2/generic/dragIcon.svg";
import { ReactComponent as DropDownArrow } from "../../Assets/icons/dropDownArrow.svg";
import { ReactComponent as PlusIcon } from "../../Assets/icons/PlusIcon.svg";
import PrimaryButton from "../GenericComponents/PrimaryButton";
import PrimaryButtonRemove from "../GenericComponents/PrimaryButtonRemove";
import DraggableList, { useDragHandle } from "../GenericComponents/DraggableList";
import PollOption from "./PollOption";
import {
  MAX_OPTIONS,
  QUESTION_MAX_LENGTH,
  MAX_POLLS,
  POLL_STATUS_DRAFT, 
  POLL_STATUS_LAUNCHED, 
  POLL_STATUS_ENDED,
  ANSWER_TYPE_SINGLE,
  ANSWER_TYPE_MULTIPLE
} from "../../Constants/constantValues";
import PollResultsDisplay from "./PollResultsDisplay";

const OrganizerPresenterPollView = ({
  polls,
  expandedPollId,
  savingPollId,
  onToggleExpand,
  onAddPoll,
  onSavePoll,
  onLaunchPoll,
  onEndPoll,
  onDeletePoll,
  onUpdateQuestion,
  onUpdateAnswerType,
  onUpdateOption,
  onAddOption,
  onDeleteOption,
  onReorderPolls,
  onReorderOptions,
  isPollModified,
  loading
}) => {
  const { t } = useTranslate();
  const optionInputRefs = useRef({});
  const [focusTargetPollId, setFocusTargetPollId] = useState(null);
  const previousOptionCounts = useRef({});

  useEffect(() => {
    if (focusTargetPollId) {
      const poll = polls.find(p => p.id === focusTargetPollId);
      if (poll) {
        const previousCount = previousOptionCounts.current[poll.id] || 0;
        const currentCount = poll.options.length;
        
        if (currentCount > previousCount) {
          const lastOption = poll.options[poll.options.length - 1];
          if (lastOption && optionInputRefs.current[lastOption.id]) {
            setTimeout(() => {
              optionInputRefs.current[lastOption.id]?.focus();
            }, 50);
          }
          setFocusTargetPollId(null);
        }

        previousOptionCounts.current[poll.id] = currentCount;
      }
    }
    
    polls.forEach(poll => {
      if (!previousOptionCounts.current[poll.id]) {
        previousOptionCounts.current[poll.id] = poll.options.length;
      }
    });
  }, [polls, focusTargetPollId]);

  const activePoll = polls.find(p => p.status === POLL_STATUS_LAUNCHED);
  const draftPolls = polls.filter(p => p.status === POLL_STATUS_DRAFT);
  const endedPolls = polls.filter(p => p.status === POLL_STATUS_ENDED);

  const DraggablePollHandle = ({ children }) => {
    const dragHandleProps = useDragHandle();
    return (
      <PollDragHandle 
        className="drag-handle"
        {...dragHandleProps}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </PollDragHandle>
    );
  };

  const handleAddOptionClick = (pollId) => {
    setFocusTargetPollId(pollId);
    previousOptionCounts.current[pollId] = polls.find(p => p.id === pollId)?.options.length || 0;
    onAddOption(pollId);
  };

  const renderPollCard = (poll, index) => {
    const isExpanded = expandedPollId === poll.id;
    const isTempPoll = String(poll.id).startsWith("temp-");
    const isSavingThis = savingPollId === poll.id;
    const hasUnsavedChanges = isPollModified(poll.id);
    const canEdit = poll.status === POLL_STATUS_DRAFT;
    const isActive = poll.status === POLL_STATUS_LAUNCHED;
    
    return (
      <PollCard key={poll.id} $expanded={isExpanded}>
        <PollHeader onClick={() => onToggleExpand(poll.id)}>
          {canEdit && (
            <DraggablePollHandle>
              <DragIcon width={16} height={16} />
            </DraggablePollHandle>
          )}
          {!canEdit && <PollNumber>#{index + 1}</PollNumber>}
          <QuestionPreview>
            {poll.question_text || t("conferences.untitled_poll")}
          </QuestionPreview>
          <StatusBadge $status={poll.status}>
            {t(`conferences.poll_status_${poll.status}`)}
          </StatusBadge>
          <ExpandIconWrapper $expanded={isExpanded}>
            <DropDownArrow width={12} height={12} />
          </ExpandIconWrapper>
        </PollHeader>

        {isExpanded && (
          <PollBody>
            <Group>
              <SubtitleRow>
                <Subtitle>{t("conferences.question")}</Subtitle>
                <CharCount $warning={poll.question_text?.length > QUESTION_MAX_LENGTH * 0.9}>
                  {poll.question_text?.length || 0}/{QUESTION_MAX_LENGTH}
                </CharCount>
              </SubtitleRow>
              <QuestionInput
                value={poll.question_text}
                onChange={(e) => onUpdateQuestion(poll.id, e.target.value)}
                placeholder={t("conferences.enter_question")}
                maxLength={QUESTION_MAX_LENGTH}
                disabled={!canEdit}
                readOnly={!canEdit}
              />
            </Group>

            <Group>
              <Subtitle>{t("conferences.answer_type")}</Subtitle>
              <ToggleWrapper>
                <ToggleOption
                  type="button"
                  $active={poll.answer_type === ANSWER_TYPE_SINGLE}
                  $position="left"
                  onClick={() => canEdit && onUpdateAnswerType(poll.id, ANSWER_TYPE_SINGLE)}
                  disabled={!canEdit}
                >
                  <ToggleIconWrapper>
                    <RadioIcon width={16} height={16} />
                  </ToggleIconWrapper>
                  {t("conferences.single_choice")}
                </ToggleOption>
                <ToggleOption
                  type="button"
                  $active={poll.answer_type === ANSWER_TYPE_MULTIPLE}
                  $position="right"
                  onClick={() => canEdit && onUpdateAnswerType(poll.id, ANSWER_TYPE_MULTIPLE)}
                  disabled={!canEdit}
                >
                  <ToggleIconWrapper>
                    <MultipleChoiceIcon width={16} height={16} />
                  </ToggleIconWrapper>
                  {t("conferences.multiple_choice")}
                </ToggleOption>
              </ToggleWrapper>
            </Group>

            <Group>
              <SubtitleRow>
                <Subtitle>
                  {(isActive || poll.status === POLL_STATUS_ENDED) && poll.results 
                    ? <>
                        {t("conferences.results")}
                        {" • "}{poll.total_responses || 0} {poll.total_responses === 1 ? t("conferences.vote") : t("conferences.votes")}
                      </>
                    : <>
                        {t("conferences.options")} ({poll.options.length}/{MAX_OPTIONS})
                      </>
                  }
                </Subtitle>
                {canEdit && !(isActive || poll.status === POLL_STATUS_ENDED) && (
                  <AddOptionButton
                    type="button"
                    onClick={() => handleAddOptionClick(poll.id)}
                    disabled={poll.options.length >= MAX_OPTIONS}
                  >
                    <PlusIcon width={9} height={9} />
                    {t("conferences.add_option")}
                  </AddOptionButton>
                )}
              </SubtitleRow>

              {(isActive || poll.status === POLL_STATUS_ENDED) && poll.results ? (
                <>
                  <PollResultsDisplay 
                    poll={poll} 
                    variant="organizer" 
                  />
                </>
              ) : (
                <DraggableList
                  items={poll.options}
                  onReorder={(newOptions) => onReorderOptions(poll.id, newOptions)}
                  disabled={!canEdit}
                  renderItem={(option, optIndex, isOptionDragging) => (
                    <PollOption
                      key={option.id}
                      poll={poll}
                      option={option}
                      optionIndex={optIndex}
                      isDragging={isOptionDragging}
                      canEdit={canEdit}
                      onUpdateText={onUpdateOption}
                      onDelete={onDeleteOption}
                      optionInputRef={(el) => (optionInputRefs.current[option.id] = el)}
                    />
                  )}
                />
              )}
            </Group>

            <Actions>
              {canEdit && (isTempPoll || hasUnsavedChanges) && (
                <PrimaryButton
                  onClick={() => onSavePoll(poll.id)}
                  disabled={isSavingThis || !hasUnsavedChanges}
                >
                  {isSavingThis ? t("general.saving") : t("general.save") + " " + t("conferences.poll")}
                </PrimaryButton>
              )}

              {canEdit && !isTempPoll && !hasUnsavedChanges && !activePoll && (
                <PrimaryButton
                  onClick={() => onLaunchPoll(poll.id)}
                  disabled={loading}
                >
                  {t("conferences.launch_poll")}
                </PrimaryButton>
              )}

              {isActive && (
                <PrimaryButtonRemove
                  onClick={() => onEndPoll(poll.id)}
                  disabled={loading}
                >
                  {t("conferences.end_poll")}
                </PrimaryButtonRemove>
              )}

              {canEdit && (
                <PrimaryButtonRemove
                  onClick={() => onDeletePoll(poll.id)}
                  disabled={loading}
                >
                  {t("general.delete") + " " + t("conferences.poll")}
                </PrimaryButtonRemove>
              )}

              {!canEdit && poll.status !== POLL_STATUS_LAUNCHED && (
                <InfoText>{t("conferences.cannot_edit_active_poll")}</InfoText>
              )}
            </Actions>
          </PollBody>
        )}
      </PollCard>
    );
  };

  return (
    <PollsList>
      {activePoll && (
        <Section>
          <SectionTitle>{t("conferences.poll_status_launched")}</SectionTitle>
          {renderPollCard(activePoll, 0)}
        </Section>
      )}

      <Section>
        <SectionHeader>
          {draftPolls.length > 0 && ( 
            <SectionTitle>{t("conferences.poll_status_draft")}</SectionTitle>
          )}
          {polls.length < MAX_POLLS && (
            <AddPollButton onClick={onAddPoll}>
              <EditIcon width={16} height={16} />
              {t("conferences.add_new_poll")}
            </AddPollButton>
          )}
        </SectionHeader>

        <DraggableList
          items={draftPolls}
          onReorder={onReorderPolls}
          renderItem={(poll, index, isDragging) => 
            renderPollCard(poll, index, isDragging)
          }
        />

        {draftPolls.length === 0 && polls.length === 0 && (
          <EmptyStateWrapper>
            <NoPollsIcon width={350} height={180} opacity={0.6} />
            <EmptyStateTitle>{t("conferences.no_polls_created")}</EmptyStateTitle>
            <EmptyStateText>{t("conferences.polls_empty_state_message")}</EmptyStateText>
          </EmptyStateWrapper>
        )}
      </Section>

      {endedPolls.length > 0 && (
        <Section>
          <SectionTitle>{t("conferences.poll_status_ended")}</SectionTitle>
          {endedPolls.map((poll, index) => renderPollCard(poll, index))}
        </Section>
      )}
    </PollsList>
  );
};

export default OrganizerPresenterPollView;

const PollsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 16px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  ${({ children }) => !children[0] && `
    justify-content: flex-end;
  `}
`;

const SectionTitle = styled.h4`
  font-size: 11px;
  font-weight: 700;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin: 0;
`;

const AddPollButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid ${({ theme }) => theme.menu.selectedTextColor};
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.menu.selectedTextColor};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  svg {
    fill: ${({ theme }) => theme.menu.selectedTextColor};
  }
  
  &:hover {
    background: ${({ theme }) => theme.menu.selectedTextColor};
    color: #FFFFFF;
    
    svg {
      fill: #FFFFFF;
    }
  }
  
  &:active {
    transform: scale(0.97);
  }
`;

const PollNumber = styled.span`
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-weight: 600;
  font-size: 13px;
  min-width: 30px;
`;

const QuestionPreview = styled.span`
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-weight: 500;
  font-size: 14px;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatusBadge = styled.span`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme, $status }) => {
    if ($status === "launched") return "#FFFFFF";
    if ($status === "ended") return "#FFFFFF";
    return theme.menu.secondaryTextColor;
  }};
  background: ${({ theme, $status }) => {
    if ($status === "launched") return theme.valid_green;
    if ($status === "ended") return theme.menu.secondaryTextColor;
    return theme.menu.background;
  }};
  border: ${({ theme, $status }) => 
    $status === "draft" ? `1px solid ${theme.sidebar.borderColor}` : "none"
  };
`;

const ExpandIconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  transform: ${({ $expanded }) => ($expanded ? "rotate(180deg)" : "rotate(0)")};
  transition: transform 0.2s ease;
  margin-left: 8px;
  
  svg {
    fill: ${({ theme }) => theme.menu.secondaryTextColor};
  }
`;

const PollBody = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  border-top: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  background: ${({ theme }) => theme.menu.background};
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin: 0;
`;

const SubtitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CharCount = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme, $warning }) =>
    $warning ? theme.menu.deleteColor : theme.menu.secondaryTextColor};
`;

const QuestionInput = styled.textarea`
  width: 100%;
  min-height: 50px;
  padding: 14px;
  border: 2px solid ${({ theme }) => theme.sidebar.borderColor};
  border-radius: 6px;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  color: ${({ theme }) => theme.menu.primaryTextColor};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  resize: vertical;
  opacity: ${({ readOnly }) => (readOnly ? 0.6 : 1)};
  cursor: ${({ readOnly }) => (readOnly ? "default" : "text")};
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.menu.selectedTextColor};
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.menu.secondaryTextColor};
    opacity: 0.6;
  }
`;

const ToggleWrapper = styled.div`
  display: flex;
  border: 2px solid ${({ theme }) => theme.sidebar.borderColor};
  border-radius: 8px;
  overflow: hidden;
  background: ${({ theme }) => theme.menu.groupContentBackground};
`;

const ToggleIconWrapper = styled.span`
  display: flex;
  align-items: center;
  margin-right: 6px;
  
  svg {
    fill: currentColor;
  }
`;

const ToggleOption = styled.button`
  flex: 1;
  padding: 10px 16px;
  border: none;
  background: ${({ theme, $active }) =>
    $active ? theme.menu.selectedTextColor : "transparent"};
  color: ${({ theme, $active }) =>
    $active ? "#FFFFFF" : theme.menu.primaryTextColor};
  font-size: 12px;
  font-weight: 600;
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
  transition: all 0.2s ease;
  opacity: ${({ disabled, $active }) => (disabled ? 0.5 : $active ? 1 : 0.8)};
  border-right: ${({ $position, theme }) =>
    $position === "left" ? `2px solid ${theme.sidebar.borderColor}` : "none"};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: ${({ theme, $active }) =>
      $active ? theme.menu.selectedTextColor : theme.menu.background};
    opacity: 1;
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }
`;

const AddOptionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.menu.selectedTextColor};
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.2s ease;
  
  &:active:not(:disabled) {
    transform: scale(0.97);
  }
`;

const PollDragHandle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: grab;
  padding: 4px 6px;
  border-radius: 6px;
  transition: all 0.2s ease;
  
  svg {
    opacity: 0.4;
    transition: opacity 0.2s ease;
    color: ${({ theme }) => theme.menu.primaryTextColor};
  }
  
  &:hover {
    background: ${({ theme }) => theme.menu.groupContentBackground};
    
    svg {
      opacity: 0.7;
    }
  }
  
  &:active {
    cursor: grabbing;
    background: ${({ theme }) => theme.sidebar.borderColor}30;
    
    svg {
      opacity: 1;
    }
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: flex-end;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.sidebar.borderColor};
`;

const InfoText = styled.p`
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 13px;
  margin: 0;
  font-style: italic;
`;

const PollCard = styled.div`
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  border-radius: 8px;
  background: ${({ theme, $isDragging }) =>
    $isDragging
      ? theme.menu.selectedOpionBackground
      : theme.menu.groupContentBackground};
  overflow: hidden;
  transition: all 0.2s ease;
  box-shadow: ${({ $isDragging }) =>
    $isDragging ? "0 8px 16px rgba(0, 0, 0, 0.4)" : "none"};
`;

const PollHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  cursor: pointer;
  user-select: none;
  &:hover {
    background: ${({ theme }) => theme.menu.background};
  }
`;

const EmptyStateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 650px;
  padding: 40px 20px;
  text-align: center;
`;

const EmptyStateTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  margin: 16px 0 8px 0;
`;

const EmptyStateText = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  line-height: 1.5;
  margin: 0;
`;